/**
 * Configurações do canal: preço (taxas, margem geral, alertas), regras de margem por categoria/produto,
 * estoque e logística. Cada salvamento recalcula a tabela de preço e estoque do canal.
 */
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { computePrice, MELI_FIXED_FEE_TABLE } from '@berytus/pricing';
import type { CategoryCount, ChannelSettings as Settings, ChannelSettingsResponse, Page, ProductRow } from '@berytus/contracts';
import { api, brl, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { useMe } from '../components/AuthGate.tsx';
import { titleOf } from './Catalog.tsx';
import { Head, useChannelTab, useMeliAccount, useUrlState } from './MeliChannel.tsx';
import { AttrFilterButton } from '../components/AttrFilter.tsx';
import { can } from '@berytus/contracts';

type Tab = 'preco' | 'regras' | 'estoque' | 'bloqueios' | 'envio' | 'logistica';
const TABS: [Tab, string][] = [['preco', 'Preço'], ['regras', 'Regras de margem'], ['estoque', 'Estoque'], ['bloqueios', 'Bloqueios'], ['envio', 'Envio ao canal'], ['logistica', 'Logística']];

export function ChannelSettingsPage() {
  const { account, isPending, error } = useMeliAccount();
  const ch = useChannelTab();
  const [params, set] = useUrlState();
  const tab = (params.get('aba') as Tab) ?? 'preco';
  const q = useQuery({
    queryKey: ['meli', 'settings', account?.id],
    queryFn: () => api<ChannelSettingsResponse>(`/channels/accounts/${account!.id}/settings`),
    enabled: !!account,
  });

  return (
    <>
      <Head title="Configurações do canal">
        <p>Como a Berytus calcula preço e estoque para {ch.code === 'shopee' ? 'a' : 'o'} {ch.name}. Cada alteração recalcula a tabela de preço e estoque na hora.</p>
      </Head>
      {isPending || q.isPending ? (account || isPending ? <Loading /> : null) : null}
      {error || q.error ? <ErrorBox error={error ?? q.error} /> : null}
      {!isPending && !account && <div className="panel empty"><b>Nenhuma conta conectada</b>Conecte {ch.code === 'shopee' ? 'a' : 'o'} {ch.name} em Integração de canais.</div>}
      {q.data && (
        <div className="stack-y">
          <div className="seg" role="tablist" aria-label="Seções">
            {TABS.filter(([k]) => !(ch.code === 'ifood' && (k === 'logistica' || k === 'envio'))).map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-pressed={tab === k} onClick={() => set({ aba: k === 'preco' ? null : k })}>{l}</button>
            ))}
          </div>
          {tab === 'preco' && <PriceForm data={q.data} />}
          {tab === 'regras' && <Rules data={q.data} />}
          {tab === 'estoque' && <StockForm data={q.data} />}
          {tab === 'envio' && <SyncForm data={q.data} />}
          {tab === 'bloqueios' && <>{ch.code === 'shopee' && <PharmaLock data={q.data} />}<Blocks accountId={q.data.accountId} /></>}
          {tab === 'logistica' && ch.code === 'shopee' && <ShopeeLogisticsForm accountId={q.data.accountId} />}
          {tab === 'logistica' && ch.code !== 'shopee' && (
            <div className="panel empty"><b>Em breve</b>Modalidades de envio, Full, prazo de despacho e custo de frete entram aqui, junto com a parte de pedidos e expedição.</div>
          )}
        </div>
      )}
    </>
  );
}

function useSaveSettings(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (s: Settings) => api<{ stats: { products: number; withPrice: number } }>(`/channels/accounts/${accountId}/settings`, { method: 'PUT', json: s }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meli'] }),
  });
}

function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {hint && <span className="small muted">{hint}</span>}
    </div>
  );
}

const num = (v: string) => (v.trim() === '' ? NaN : Number(v.replace(',', '.')));

function PriceForm({ data }: { data: ChannelSettingsResponse }) {
  const ch = useChannelTab();
  const admin = can(useMe().tenant.access, 'channel_settings', 'write');
  const save = useSaveSettings(data.accountId);
  const [f, setF] = useState(() => ({ ...data.settings }));
  const [txt, setTxt] = useState(() => ({
    takeRatePct: String(data.settings.takeRatePct),
    takeRatePremiumPct: data.settings.takeRatePremiumPct == null ? '' : String(data.settings.takeRatePremiumPct),
    extraPct: String(data.settings.extraPct),
    extraFixed: String(data.settings.extraFixed),
    fixedFee: String(data.settings.fixedFee),
    fixedFeeBelow: data.settings.fixedFeeBelow == null ? '' : String(data.settings.fixedFeeBelow),
    defaultMarginPct: String(data.settings.defaultMarginPct),
    minMarginAlertPct: String(data.settings.minMarginAlertPct),
  }));
  useEffect(() => setF((x) => ({ ...x, ...data.settings })), [data.settings]);

  const parsed: Settings = {
    ...f,
    takeRatePct: num(txt.takeRatePct),
    takeRatePremiumPct: txt.takeRatePremiumPct.trim() === '' ? null : num(txt.takeRatePremiumPct),
    extraPct: num(txt.extraPct || '0'),
    extraFixed: num(txt.extraFixed || '0'),
    fixedFee: num(txt.fixedFee || '0'),
    fixedFeeBelow: txt.fixedFeeBelow.trim() === '' ? null : num(txt.fixedFeeBelow),
    defaultMarginPct: num(txt.defaultMarginPct),
    minMarginAlertPct: num(txt.minMarginAlertPct),
  };
  const invalid = Object.values(parsed).some((v) => typeof v === 'number' && Number.isNaN(v));
  const example = useMemo(
    () => (invalid ? null : computePrice(10, parsed.defaultMarginPct, {
      ...parsed,
      takeRatePct: parsed.defaultListingType === 'gold_pro' && parsed.takeRatePremiumPct != null ? parsed.takeRatePremiumPct : parsed.takeRatePct,
      fixedFeeTable: parsed.fixedFeeMode === 'meli_table' ? MELI_FIXED_FEE_TABLE : null,
    })),
    [JSON.stringify(parsed)], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!invalid) save.mutate(parsed);
  };
  const t = (k: keyof typeof txt) => ({
    className: 'input',
    inputMode: 'decimal' as const,
    value: txt[k],
    disabled: !admin,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setTxt({ ...txt, [k]: e.target.value }),
  });

  return (
    <form className="stack-y" onSubmit={submit}>
      <section className="panel stack-y" style={{ gap: 14 }}>
        <h2 style={{ margin: 0 }}>Quem define o preço</h2>
        <div className="seg" role="radiogroup" aria-label="Origem do preço">
          <button type="button" aria-pressed={f.pricingMode === 'engine'} disabled={!admin} onClick={() => setF({ ...f, pricingMode: 'engine' })}>A Berytus calcula</button>
          <button type="button" aria-pressed={f.pricingMode === 'external'} disabled={!admin} onClick={() => setF({ ...f, pricingMode: 'external' })}>Envio o preço pela API</button>
        </div>
        <p className="small muted" style={{ margin: 0 }}>
          {f.pricingMode === 'engine'
            ? 'Preço = custo e taxas do canal + margem (do produto, da categoria ou a geral). Preço na mão e buy box aplicado sempre têm prioridade.'
            : 'Vale o preço que o seu sistema envia pela API. Produto sem preço enviado usa o padrão: custo + taxas do canal + 20% de margem (com alerta).'}
        </p>
      </section>

      <section className="panel stack-y" style={{ gap: 14 }}>
        <h2 style={{ margin: 0 }}>Taxas {ch.code === 'shopee' ? 'da' : 'do'} {ch.name}</h2>
        <div className="cols" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          {ch.code !== 'mercadolivre' ? (
            <Field label="Comissão %" hint={`Comissão + taxas ${ch.code === 'shopee' ? 'da Shopee (confira no Seller Centre)' : 'do iFood (confira no contrato)'}`}><input {...t('takeRatePct')} /></Field>
          ) : (
            <>
              <Field label="Comissão Clássico %" hint="Padrão do Mercado Livre: 16%"><input {...t('takeRatePct')} /></Field>
              <Field label="Comissão Premium %" hint="Confira no seu contrato. Vazio = igual ao Clássico"><input {...t('takeRatePremiumPct')} /></Field>
              <Field label="Tipo de anúncio padrão" hint="Usado no cálculo do preço e nas publicações">
                <select className="select" value={f.defaultListingType} disabled={!admin} onChange={(e) => setF({ ...f, defaultListingType: e.target.value as Settings['defaultListingType'] })}>
                  <option value="gold_special">Clássico</option>
                  <option value="gold_pro">Premium</option>
                </select>
              </Field>
            </>
          )}
          <Field label="Outras taxas em % do preço" hint="Imposto, antecipação, campanhas…"><input {...t('extraPct')} /></Field>
          {ch.code === 'mercadolivre' && (
            <Field label="Custo fixo por unidade" hint="Tabela do Mercado Livre: abaixo de R$ 12,50 metade do preço; até R$ 29 R$ 6,25; até R$ 50 R$ 6,50; até R$ 79 R$ 6,75">
              <select className="select" value={f.fixedFeeMode} disabled={!admin} onChange={(e) => setF({ ...f, fixedFeeMode: e.target.value as Settings['fixedFeeMode'] })}>
                <option value="meli_table">Tabela do Mercado Livre por faixa de preço</option>
                <option value="flat">Valor único (abaixo)</option>
              </select>
            </Field>
          )}
          {(ch.code !== 'mercadolivre' || f.fixedFeeMode === 'flat') && <>
            <Field label="Taxa fixa por unidade (R$)" hint="Cobrada pelo canal em itens baratos"><input {...t('fixedFee')} /></Field>
            <Field label="…para preços abaixo de (R$)" hint={ch.code !== 'mercadolivre' ? 'Vazio = sempre' : 'Mercado Livre: R$ 79. Vazio = sempre'}><input {...t('fixedFeeBelow')} /></Field>
          </>}
          <Field label="Outros custos fixos por unidade (R$)" hint="Embalagem, etiqueta…"><input {...t('extraFixed')} /></Field>
        </div>
      </section>

      <section className="panel stack-y" style={{ gap: 14 }}>
        <h2 style={{ margin: 0 }}>Margem e limites</h2>
        <div className="cols" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <Field label="Margem geral %" hint="Sobre o preço de venda, depois das taxas. Categoria e produto podem ter a sua."><input {...t('defaultMarginPct')} /></Field>
          <Field label="Alertar margem abaixo de %"><input {...t('minMarginAlertPct')} /></Field>
          <Field label="Arredondamento">
            <select className="select" value={f.rounding} disabled={!admin} onChange={(e) => setF({ ...f, rounding: e.target.value as Settings['rounding'] })}>
              <option value="none">Centavos exatos</option>
              <option value="90">Terminar em ,90</option>
              <option value="99">Terminar em ,99</option>
            </select>
          </Field>
          <Field label="Teto no PMC">
            <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'center', height: 36 }}>
              <input type="checkbox" className="check" checked={f.capAtPmc} disabled={!admin} onChange={(e) => setF({ ...f, capAtPmc: e.target.checked })} />
              Nunca passar do preço máximo ao consumidor
            </label>
          </Field>
        </div>
        <div className="notice small">
          {example ? (
            <>Exemplo com custo de <b>R$ 10,00</b> e a margem geral: preço <b>{brl(example.price)}</b> · taxas {brl(example.fees)} · lucro {brl(example.profit)} ({example.marginPct.toLocaleString('pt-BR')}%).</>
          ) : invalid ? 'Preencha os campos com números.' : 'Comissão + taxas + margem passam de 100%: não há preço possível.'}
        </div>
      </section>

      {admin && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="btn primary" type="submit" disabled={invalid || save.isPending}>{save.isPending ? 'Salvando e recalculando…' : 'Salvar e recalcular preços'}</button>
          {save.data && <span className="small muted">Tabela recalculada: {int(save.data.stats.withPrice)} de {int(save.data.stats.products)} produtos com preço.</span>}
        </div>
      )}
      {save.error && <ErrorBox error={save.error} />}
    </form>
  );
}

function StockForm({ data }: { data: ChannelSettingsResponse }) {
  const admin = can(useMe().tenant.access, 'channel_settings', 'write');
  const save = useSaveSettings(data.accountId);
  const [f, setF] = useState(() => ({ ...data.settings }));
  const [safety, setSafety] = useState(String(data.settings.safetyStock));
  const [max, setMax] = useState(data.settings.maxStock == null ? '' : String(data.settings.maxStock));
  const [pct, setPct] = useState(String(data.settings.stockPct ?? 100));
  const pctNum = num(pct);
  const pctBad = !(pctNum > 0 && pctNum <= 100);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (pctBad) return;
    save.mutate({ ...data.settings, stockMode: f.stockMode, safetyStock: Number(safety) || 0, maxStock: max.trim() ? Number(max) : null, stockPct: pctNum });
  };
  return (
    <form className="stack-y" onSubmit={submit}>
      <section className="panel stack-y" style={{ gap: 14 }}>
        <h2 style={{ margin: 0 }}>Estoque enviado ao canal</h2>
        <div className="seg" role="radiogroup">
          <button type="button" aria-pressed={f.stockMode === 'engine'} disabled={!admin} onClick={() => setF({ ...f, stockMode: 'engine' })}>Estoque da Berytus</button>
          <button type="button" aria-pressed={f.stockMode === 'external'} disabled={!admin} onClick={() => setF({ ...f, stockMode: 'external' })}>Envio o estoque pela API</button>
        </div>
        <p className="small muted" style={{ margin: 0 }}>
          Estoque do canal = (disponível na loja da conexão − estoque de segurança do canal) × % do canal, até o máximo.
          O % arredonda para baixo (80% de 1 unidade = 0). Kits usam o estoque do produto base.
          {f.stockMode === 'external' && ' No modo API, vale o estoque enviado; sem envio, usa o cálculo acima.'}
        </p>
        <div className="cols" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <Field label="Estoque de segurança do canal (un.)" hint="Reserva para não vender o que pode faltar"><input className="input" inputMode="numeric" value={safety} disabled={!admin} onChange={(e) => setSafety(e.target.value)} /></Field>
          <Field label="Máximo por anúncio (un.)" hint="Vazio = sem limite"><input className="input" inputMode="numeric" value={max} disabled={!admin} onChange={(e) => setMax(e.target.value)} /></Field>
          <Field label="% do estoque para este canal" hint={pctBad ? 'Entre 1 e 100.' : 'Ex.: 100 no Mercado Livre, 80 na Shopee'}><input className="input" inputMode="decimal" value={pct} disabled={!admin} onChange={(e) => setPct(e.target.value)} /></Field>
        </div>
      </section>
      {admin && <div><button className="btn primary" type="submit" disabled={save.isPending || pctBad}>{save.isPending ? 'Salvando…' : 'Salvar e recalcular estoque'}</button></div>}
      {save.error && <ErrorBox error={save.error} />}
    </form>
  );
}

interface SyncPreview {
  mode: Settings['syncMode'];
  listings: number;
  changes: number;
  priceChanges: number;
  stockChanges: number;
  skipped: Record<string, number>;
  errors: number;
  sample: { externalId: string; sku: string; title: string | null; price?: { from: number | null; to: number }; quantity?: { from: number | null; to: number } }[];
}
interface SyncError { productId: string; sku: string; title: string | null; error: string; at: string }

const SKIP_LABEL: Record<string, string> = {
  fulfillment_stock: 'no Full (estoque é do Mercado Livre)',
  no_price: 'sem preço na tabela',
  default_price: 'com preço padrão de emergência (não vai)',
  price_guard: 'acima da trava de variação de preço',
  below_min: 'abaixo do mínimo da categoria (não vão)',
};
const MODE_LABEL: Record<Settings['syncMode'], string> = { off: 'Desligado', stock: 'Só estoque', price: 'Só preço', price_stock: 'Preço e estoque' };

function SyncForm({ data }: { data: ChannelSettingsResponse }) {
  const ch = useChannelTab();
  const admin = can(useMe().tenant.access, 'channel_settings', 'write');
  const qc = useQueryClient();
  const save = useSaveSettings(data.accountId);
  const [mode, setMode] = useState<Settings['syncMode']>(data.settings.syncMode ?? 'off');
  const [guard, setGuard] = useState(String(data.settings.priceGuardPct ?? 50));
  const [belowMin, setBelowMin] = useState<'min' | 'skip'>(data.settings.belowMinPrice ?? 'min');
  const previewMode = mode === 'off' ? 'price_stock' : mode;
  const preview = useQuery({
    queryKey: ['meli', 'sync-preview', data.accountId, previewMode],
    queryFn: () => api<SyncPreview>(`/channels/accounts/${data.accountId}/sync/preview?mode=${previewMode}`),
  });
  const errors = useQuery({
    queryKey: ['meli', 'sync-errors', data.accountId],
    queryFn: () => api<SyncError[]>(`/channels/accounts/${data.accountId}/sync/errors`),
  });
  const run = useMutation({
    mutationFn: () => api(`/channels/accounts/${data.accountId}/sync/run`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meli'] }),
  });
  const all = useMutation({
    mutationFn: () => api<{ accounts: { id: string }[] }>('/channels/sync-mode', { method: 'POST', json: { mode, priceGuardPct: num(guard) } }),
    onSuccess: () => qc.invalidateQueries(),
  });
  const guardNum = num(guard);
  const guardBad = !(guardNum >= 0 && guardNum <= 1000);
  const dirty = mode !== data.settings.syncMode || guardNum !== data.settings.priceGuardPct || belowMin !== (data.settings.belowMinPrice ?? 'min');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (guardBad) return;
    if (mode !== 'off' && data.settings.syncMode === 'off' && preview.data && preview.data.changes > 0
      && !window.confirm(`Ligar o envio vai alterar ${int(preview.data.changes)} anúncio(s) ${ch.na} na próxima rodada. Continuar?`)) return;
    save.mutate({ ...data.settings, syncMode: mode, priceGuardPct: guardNum, belowMinPrice: belowMin });
  };
  const last = data.settings.lastSyncStats;
  const p = preview.data;

  return (
    <form className="stack-y" onSubmit={submit}>
      <section className="panel stack-y" style={{ gap: 14 }}>
        <h2 style={{ margin: 0 }}>Enviar a tabela para os anúncios</h2>
        <div className="seg" role="radiogroup" aria-label="Envio ao canal">
          {(['off', 'price', 'stock', 'price_stock'] as const).map((m) => (
            <button key={m} type="button" aria-pressed={mode === m} disabled={!admin} onClick={() => setMode(m)}>{MODE_LABEL[m]}</button>
          ))}
        </div>
        <p className="small muted" style={{ margin: 0 }}>
          Ligado, a Berytus envia {ch.code === 'shopee' ? 'à Shopee' : ch.code === 'ifood' ? 'ao iFood' : 'ao Mercado Livre'} o preço e/ou o estoque da tabela para os anúncios vinculados (ativos e pausados),
          só quando estão diferentes do anúncio: logo depois de cada mudança e, para garantir, a cada 15 minutos.
          Se outro sistema também altera preço ou estoque desses anúncios, desligue o envio nele antes, para os dois não brigarem.
        </p>
        <div style={{ maxWidth: 360 }}>
          <Field label="Trava de variação de preço (%)" hint={guardBad ? 'Entre 0 e 1000.' : 'Preço que muda mais que isto em relação ao anúncio não vai sozinho. 0 = sem trava.'}>
            <input className="input" inputMode="decimal" value={guard} disabled={!admin} onChange={(e) => setGuard(e.target.value)} />
          </Field>
        </div>
        <div style={{ maxWidth: 520 }}>
          <Field label="Preço abaixo do mínimo da categoria" hint="O Mercado Livre exige preço mínimo em algumas categorias (ex.: R$ 8 em absorventes e higiene). A Berytus aprende o mínimo na primeira recusa.">
            <select className="select" value={belowMin} disabled={!admin} onChange={(e) => setBelowMin(e.target.value as 'min' | 'skip')}>
              <option value="min">Enviar o mínimo da categoria</option>
              <option value="skip">Não enviar (o anúncio fica com o preço atual)</option>
            </select>
          </Field>
        </div>
      </section>

      <section className="panel stack-y" style={{ gap: 10 }}>
        <h2 style={{ margin: 0 }}>Prévia {mode === 'off' && <span className="small muted">(se ligar preço e estoque)</span>}</h2>
        {preview.isPending && <Loading />}
        {preview.error && <ErrorBox error={preview.error} />}
        {p && (
          <>
            <div className="small">
              <b>{int(p.changes)}</b> de {int(p.listings)} anúncios vinculados mudariam agora
              {p.mode !== 'stock' && <> · {int(p.priceChanges)} de preço</>}{p.mode !== 'price' && <> · {int(p.stockChanges)} de estoque</>}
            </div>
            {Object.entries(p.skipped).filter(([, n]) => n > 0).map(([k, n]) => (
              <div key={k} className="small muted">{int(n)} anúncio(s) {SKIP_LABEL[k] ?? k}</div>
            ))}
            {p.sample.length > 0 && (
              <div className="grid-wrap">
                <table className="grid" style={{ width: "100%" }}>
                  <thead><tr><th>Anúncio</th><th>SKU</th><th>Produto</th><th className="num">Preço</th><th className="num">Estoque</th></tr></thead>
                  <tbody>
                    {p.sample.slice(0, 20).map((c) => (
                      <tr key={c.externalId + c.sku}>
                        <td className="code">{c.externalId}</td>
                        <td className="code">{c.sku}</td>
                        <td>{c.title}</td>
                        <td className="num">{c.price ? <>{c.price.from == null ? '—' : brl(c.price.from)} → <b>{brl(c.price.to)}</b></> : '—'}</td>
                        <td className="num">{c.quantity ? <>{c.quantity.from ?? '—'} → <b>{c.quantity.to}</b></> : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      <section className="panel stack-y" style={{ gap: 10 }}>
        <h2 style={{ margin: 0 }}>Último envio</h2>
        {data.settings.lastSyncAt ? (
          <div className="small">
            {new Date(data.settings.lastSyncAt).toLocaleString('pt-BR')}: {int(last?.sent ?? 0)} enviado(s), {int(last?.failed ?? 0)} com erro, de {int(last?.checked ?? 0)} conferidos.
          </div>
        ) : <span className="small muted">Nenhum envio ainda.</span>}
        {errors.data && errors.data.length > 0 && (
          <div className="grid-wrap">
            <table className="grid" style={{ width: "100%" }}>
              <thead><tr><th>SKU</th><th>Produto</th><th>Motivo</th></tr></thead>
              <tbody>
                {errors.data.slice(0, 50).map((e) => (
                  <tr key={e.productId}><td className="code">{e.sku}</td><td>{e.title}</td><td className="small">{e.error}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {admin && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn primary" type="submit" disabled={save.isPending || guardBad || !dirty}>{save.isPending ? 'Salvando…' : 'Salvar'}</button>
          <button className="btn" type="button" disabled={run.isPending || data.settings.syncMode === 'off'} onClick={() => run.mutate()}>
            {run.isSuccess ? 'Envio na fila' : 'Enviar agora'}
          </button>
          <button className="btn quiet" type="button" disabled={all.isPending || guardBad}
            title="Usa este modo de envio e esta trava em todas as contas do Mercado Livre e lojas da Shopee"
            onClick={() => window.confirm(`Usar "${MODE_LABEL[mode]}" (trava ${guardNum}%) em todas as contas do Mercado Livre e lojas da Shopee?`) && all.mutate()}>
            {all.isSuccess ? `Aplicado em ${all.data.accounts.length} conta(s)` : 'Aplicar em todos os canais'}
          </button>
        </div>
      )}
      {(save.error || run.error || all.error) && <ErrorBox error={save.error ?? run.error ?? all.error} />}
    </form>
  );
}

type BlockScope = 'department' | 'category' | 'subcategory' | 'brand' | 'attribute' | 'product';
interface BlockRow { id: string; scope: BlockScope; value: string | null; attrValue: string | null; productSku: string | null; productTitle: string | null; label: string; products: number; note: string | null }
interface Facets {
  department: { value: string; count: number }[]; category: { value: string; count: number }[];
  subcategory: { value: string; count: number }[]; brand: { value: string; count: number }[];
  attributes: { key: string; label: string; type: string }[];
}
const SCOPES: [BlockScope, string][] = [
  ['category', 'Categoria'], ['department', 'Departamento'], ['subcategory', 'Subcategoria'], ['brand', 'Marca'], ['attribute', 'Atributo próprio'], ['product', 'Produto'],
];

/** Bloqueios do canal: itens que não vão para este canal (estoque 0 aqui, nos outros canais nada muda). */
/** Shopee: só MIP por padrão. Liberar medicamentos com tarja (lojas com licença); controlados nunca vão. */
function PharmaLock({ data }: { data: ChannelSettingsResponse }) {
  const admin = can(useMe().tenant.access, 'channel_settings', 'write');
  const save = useSaveSettings(data.accountId);
  const on = !!data.settings.allowPrescription;
  return (
    <section className="panel stack-y" style={{ gap: 10, marginBottom: 'var(--gap)' }}>
      <h2 style={{ margin: 0 }}>Medicamentos</h2>
      <p className="small muted" style={{ margin: 0 }}>
        A Shopee aceita só medicamentos isentos de prescrição (MIP). Pelos atributos padrão <b>MIP</b>, <b>Controlado</b> e <b>Psicotrópico</b> do produto
        (ou pela sugestão da Berytus, quando não informados), os medicamentos com tarja ficam bloqueados para publicar. Controlados e psicotrópicos nunca vão.
      </p>
      <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input type="checkbox" className="check" checked={on} disabled={!admin || save.isPending}
          onChange={(e) => (!e.target.checked || window.confirm('Liberar medicamentos com tarja na Shopee? Faça isso só se a loja tiver a licença aprovada pela Shopee.'))
            && save.mutate({ ...data.settings, allowPrescription: e.target.checked })} />
        Liberar medicamentos com tarja (não MIP) nesta loja
      </label>
      <AnvisaExempt data={data} admin={admin} save={save} />
      {save.error && <ErrorBox error={save.error} />}
    </section>
  );
}

/**
 * Não medicamento sem registro Anvisa: várias categorias da Shopee (absorvente, desodorante, suplemento, cosmético)
 * exigem o atributo "Código de registro ou notificação da ANVISA". O texto escolhido aqui vai nesse atributo só para
 * produto que não é medicamento e não tem registro no cadastro nem no catálogo mestre (se a lista da Shopee tiver a
 * opção de isento, vai a opção). Vazio = desligado: o item fica pendente com o motivo próprio.
 */
function AnvisaExempt({ data, admin, save }: { data: ChannelSettingsResponse; admin: boolean; save: ReturnType<typeof useSaveSettings> }) {
  const [v, setV] = useState(data.settings.shopeeAnvisaExempt ?? '');
  const current = data.settings.shopeeAnvisaExempt ?? '';
  return (
    <div className="stack-y" style={{ gap: 6, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
      <b className="small">Registro Anvisa em não medicamento</b>
      <p className="small muted" style={{ margin: 0 }}>
        Algumas categorias da Shopee (absorvente, desodorante, suplemento, cosmético) pedem o "Código de registro ou notificação da ANVISA". Para produto que <b>não é medicamento</b> e não tem
        o código no cadastro nem no catálogo mestre, preencher com o texto abaixo (se a Shopee tiver a opção de isento na lista, vai a opção). Vazio: o item fica pendente
        ("Shopee pede registro Anvisa (não medicamento)"). Medicamento nunca usa este texto. Depois de salvar, rode "Resolver categoria" para reavaliar.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input className="input" style={{ maxWidth: 280 }} placeholder="ex.: Isento de registro" maxLength={40} value={v} disabled={!admin} onChange={(e) => setV(e.target.value)} />
        <button type="button" className="btn sm" disabled={!admin || save.isPending || v.trim() === current}
          onClick={() => save.mutate({ ...data.settings, shopeeAnvisaExempt: v.trim() || null })}>Salvar</button>
      </div>
    </div>
  );
}

function Blocks({ accountId }: { accountId: string }) {
  const admin = can(useMe().tenant.access, 'channel_settings', 'write');
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['meli', 'blocks', accountId], queryFn: () => api<{ total: number; blocks: BlockRow[] }>(`/channels/accounts/${accountId}/blocks`) });
  const facets = useQuery({ queryKey: ['facets'], queryFn: () => api<Facets>('/catalog/facets') });
  const done = (d: { total: number; blocks: BlockRow[] }) => { qc.setQueryData(['meli', 'blocks', accountId], d); qc.invalidateQueries({ queryKey: ['meli'] }); };
  const add = useMutation({ mutationFn: (b: Record<string, string | boolean | undefined>) => api<{ total: number; blocks: BlockRow[] }>(`/channels/accounts/${accountId}/blocks`, { method: 'POST', json: b }), onSuccess: (d) => { done(d); setValue(''); setAttrValue('true'); setPicked(null); setProdQ(''); } });
  const del = useMutation({ mutationFn: (id: string) => api<{ total: number; blocks: BlockRow[] }>(`/channels/blocks/${id}`, { method: 'DELETE' }), onSuccess: done });
  const [scope, setScope] = useState<BlockScope>('category');
  const [value, setValue] = useState('');
  const [attrValue, setAttrValue] = useState('true');
  const [prodQ, setProdQ] = useState('');
  const [picked, setPicked] = useState<ProductRow | null>(null);
  const [allChannels, setAllChannels] = useState(false);
  const found = useQuery({
    queryKey: ['block-pick', prodQ],
    queryFn: () => api<Page<ProductRow>>(`/catalog/products?q=${encodeURIComponent(prodQ)}&pageSize=6`),
    enabled: scope === 'product' && prodQ.trim().length >= 2 && !picked,
  });
  const f = facets.data;
  const options = scope === 'attribute' ? (f?.attributes ?? []).map((a) => ({ value: a.key, label: a.label }))
    : scope === 'product' ? [] : (f?.[scope] ?? []).map((x) => ({ value: x.value, label: `${x.value} (${int(x.count)})` }));
  const attrDef = f?.attributes.find((a) => a.key === value);
  const canAdd = scope === 'product' ? !!picked : !!value && (scope !== 'attribute' || !!attrValue.trim());
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!canAdd) return;
    const base = scope === 'product' ? { scope, productId: picked!.id } : scope === 'attribute' ? { scope, value, attrValue } : { scope, value };
    add.mutate({ ...base, allChannels });
  };

  return (
    <div className="stack-y">
      <p className="small muted" style={{ margin: 0 }}>
        Item bloqueado vai com <b>estoque 0</b> para este canal e não é publicado aqui; nos outros canais nada muda.
        Kits seguem o produto base. Com o envio ao canal ligado, o estoque 0 sobe para os anúncios na próxima rodada.
      </p>
      {admin && <BulkBlock accountId={accountId} facets={f} />}
      {admin && (
        <form className="panel stack-y" style={{ gap: 10 }} onSubmit={submit}>
          <h2 style={{ margin: 0 }}>Bloquear por regra</h2>
          <p className="small muted" style={{ margin: 0 }}>Vale para tudo o que entrar depois (ex.: toda a marca ou todos os controlados).</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select className="select" value={scope} onChange={(e) => { setScope(e.target.value as BlockScope); setValue(''); setPicked(null); }} aria-label="Bloquear por" style={{ width: 190 }}>
              {SCOPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            {scope !== 'product' && (
              <select className="select" value={value} onChange={(e) => { setValue(e.target.value); setAttrValue(f?.attributes.find((a) => a.key === e.target.value)?.type === 'boolean' ? 'true' : ''); }} aria-label="Valor" style={{ flex: '1 1 260px' }}>
                <option value="">{scope === 'attribute' ? 'Atributo…' : 'Escolha…'}</option>
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
            {scope === 'attribute' && value && (attrDef?.type === 'boolean' ? (
              <select className="select" value={attrValue} onChange={(e) => setAttrValue(e.target.value)} aria-label="Valor do atributo" style={{ width: 120 }}>
                <option value="true">= sim</option><option value="false">= não</option>
              </select>
            ) : (
              <input className="input" value={attrValue} placeholder="valor" onChange={(e) => setAttrValue(e.target.value)} style={{ width: 180 }} />
            ))}
            {scope === 'product' && (picked ? (
              <span className="notice small" style={{ flex: '1 1 260px', padding: '6px 10px' }}>
                {picked.sku} · {titleOf(picked)} <button type="button" className="btn quiet sm" onClick={() => setPicked(null)}>trocar</button>
              </span>
            ) : (
              <input className="input" placeholder="Buscar produto por SKU, EAN ou nome" value={prodQ} onChange={(e) => setProdQ(e.target.value)} style={{ flex: '1 1 260px' }} />
            ))}
            <label className="small" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" className="check" checked={allChannels} onChange={(e) => setAllChannels(e.target.checked)} /> em todos os canais
            </label>
            <button className="btn primary" type="submit" disabled={!canAdd || add.isPending}>{add.isPending ? 'Bloqueando…' : 'Bloquear'}</button>
          </div>
          {scope === 'product' && !picked && found.data && found.data.items.length > 0 && (
            <div style={{ display: 'grid', gap: 4 }}>
              {found.data.items.map((p) => (
                <button key={p.id} type="button" className="btn sm" style={{ justifyContent: 'flex-start' }} onClick={() => setPicked(p)}>
                  <span className="code">{p.sku}</span> · {titleOf(p)}
                </button>
              ))}
            </div>
          )}
          {add.error && <ErrorBox error={add.error} />}
        </form>
      )}

      <section className="panel stack-y" style={{ gap: 10 }}>
        <h2 style={{ margin: 0 }}>Bloqueados neste canal {list.data && <span className="small muted">· {int(list.data.total)} produto(s)</span>}</h2>
        {list.isPending && <Loading />}
        {list.data && list.data.blocks.length === 0 && <span className="small muted">Nenhum bloqueio: tudo o que tem estoque vai para este canal.</span>}
        {list.data?.blocks.map((b) => (
          <div className="row-line" key={b.id}>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {b.scope === 'product' ? <><span className="code">{b.productSku}</span> · {b.productTitle}</> : b.label}
            </span>
            <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <span className="small muted tnum">{int(b.products)} produto(s)</span>
              {admin && <button className="btn sm" type="button" disabled={del.isPending} onClick={() => del.mutate(b.id)}>Liberar</button>}
            </span>
          </div>
        ))}
        {del.error && <ErrorBox error={del.error} />}
      </section>
    </div>
  );
}

interface BlockItem { id: string; sku: string; title: string | null; brand: string | null; category: string | null; price: number | null; stock: number | null; blockedBy: string | null }

/** Bloquear ou liberar produtos escolhidos: busca por marca, categoria, preço, atributos ou lista de SKUs. */
function BulkBlock({ accountId, facets }: { accountId: string; facets: Facets | undefined }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [skus, setSkus] = useState('');
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [blocked, setBlocked] = useState<'all' | 'blocked' | 'free'>('all');
  const [attrs, setAttrs] = useState(new URLSearchParams());
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [allChannels, setAllChannels] = useState(false);
  const filter = {
    ...(q.trim() ? { q: q.trim() } : {}), ...(skus.trim() ? { skus: skus.trim() } : {}), ...(brand.trim() ? { brand: brand.trim() } : {}),
    ...(category ? { category } : {}), ...(priceMin ? { priceMin: num(priceMin) } : {}), ...(priceMax ? { priceMax: num(priceMax) } : {}),
    ...(attrs.get('attrs') ? { attrs: attrs.get('attrs')! } : {}), blocked,
  };
  const key = JSON.stringify(filter);
  useEffect(() => { setPage(1); setSelected(new Set()); setAllMatching(false); }, [key]);
  const searching = Object.keys(filter).length > 1 || blocked !== 'all';
  const res = useQuery({
    queryKey: ['block-search', accountId, key, page],
    queryFn: () => api<{ total: number; pageSize: number; unknownSkus: string[]; items: BlockItem[] }>(
      `/channels/accounts/${accountId}/block-search?${new URLSearchParams({ ...Object.fromEntries(Object.entries(filter).map(([k, v]) => [k, String(v)])), page: String(page), pageSize: '50' })}`),
    enabled: searching,
  });
  const run = useMutation({
    mutationFn: (action: 'block' | 'unblock') => api<{ products: number; accounts: number }>(`/channels/accounts/${accountId}/blocks/bulk`, {
      method: 'POST', json: { action, allChannels, ...(allMatching ? { filter } : { productIds: [...selected] }) },
    }),
    onSuccess: () => { setSelected(new Set()); setAllMatching(false); qc.invalidateQueries({ queryKey: ['block-search'] }); qc.invalidateQueries({ queryKey: ['meli'] }); },
  });
  const items = res.data?.items ?? [];
  const allOnPage = items.length > 0 && items.every((i) => selected.has(i.id));
  const count = allMatching ? res.data?.total ?? 0 : selected.size;
  const toggle = (id: string) => { setAllMatching(false); setSelected((cur) => { const n = new Set(cur); n.has(id) ? n.delete(id) : n.add(id); return n; }); };

  return (
    <section className="panel stack-y" style={{ gap: 10 }}>
      <h2 style={{ margin: 0 }}>Bloquear produtos</h2>
      <p className="small muted" style={{ margin: 0 }}>Busque por marca, categoria, preço neste canal, atributos ou cole uma lista de SKUs; marque os produtos e bloqueie ou libere — neste canal ou em todos.</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="input" style={{ flex: '1 1 200px' }} placeholder="SKU ou nome" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Buscar" />
        <input className="input" style={{ width: 170 }} list="block-brands" placeholder="Marca" value={brand} onChange={(e) => setBrand(e.target.value)} aria-label="Marca" />
        <datalist id="block-brands">{facets?.brand.map((b) => <option key={b.value} value={b.value}>{`${int(b.count)} produto(s)`}</option>)}</datalist>
        <select className="select" style={{ width: 200 }} value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Categoria">
          <option value="">Todas as categorias</option>
          {facets?.category.map((c) => <option key={c.value} value={c.value}>{c.value} ({int(c.count)})</option>)}
        </select>
        <input className="input" style={{ width: 100 }} inputMode="decimal" placeholder="Preço de" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} aria-label="Preço mínimo" />
        <input className="input" style={{ width: 100 }} inputMode="decimal" placeholder="até" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} aria-label="Preço máximo" />
        <select className="select" style={{ width: 150 }} value={blocked} onChange={(e) => setBlocked(e.target.value as typeof blocked)} aria-label="Situação">
          <option value="all">Bloqueados e liberados</option><option value="free">Só liberados</option><option value="blocked">Só bloqueados</option>
        </select>
        <AttrFilterButton params={attrs} set={(p) => { const n = new URLSearchParams(attrs); for (const [k, v] of Object.entries(p)) (v == null ? n.delete(k) : n.set(k, v)); setAttrs(n); }} />
      </div>
      <textarea className="input" rows={2} style={{ height: 'auto', fontFamily: 'var(--mono, monospace)' }} placeholder="Lista de SKUs: 1023861, 1277006, 1217445 (vírgula, espaço ou um por linha)"
        value={skus} onChange={(e) => setSkus(e.target.value)} aria-label="Lista de SKUs" />
      {res.data?.unknownSkus.length ? <div className="notice warn small">SKU(s) não encontrados: {res.data.unknownSkus.slice(0, 30).join(', ')}{res.data.unknownSkus.length > 30 ? '…' : ''}</div> : null}
      {!searching ? <span className="small muted">Use um filtro acima para listar os produtos.</span> : res.isPending ? <Loading /> : res.error ? <ErrorBox error={res.error} /> : (
        <>
          <div className="bulkbar" role="status" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span><b>{int(res.data!.total)}</b> produto(s){count > 0 && <> · <b>{int(count)}</b> marcado(s)</>}</span>
            {allOnPage && !allMatching && res.data!.total > items.length && (
              <button className="btn sm quiet" type="button" onClick={() => setAllMatching(true)}>marcar todos os {int(res.data!.total)} da busca</button>
            )}
            <span style={{ flex: 1 }} />
            <label className="small" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" className="check" checked={allChannels} onChange={(e) => setAllChannels(e.target.checked)} /> em todos os canais
            </label>
            <button className="btn sm danger" type="button" disabled={!count || run.isPending} onClick={() => run.mutate('block')}>Bloquear {count ? int(count) : ''}</button>
            <button className="btn sm" type="button" disabled={!count || run.isPending} onClick={() => run.mutate('unblock')}>Liberar {count ? int(count) : ''}</button>
          </div>
          {run.data && <div className="notice small">{run.variables === 'block' ? 'Bloqueados' : 'Liberados'}: {int(run.data.products)} produto(s) em {int(run.data.accounts)} canal(is). O estoque vai junto na próxima rodada de envio.</div>}
          {run.error && <ErrorBox error={run.error} />}
          <div style={{ overflowX: 'auto' }}>
            <table className="simple">
              <thead><tr>
                <th style={{ width: 28 }}><input type="checkbox" className="check" aria-label="Marcar a página" checked={allOnPage || allMatching}
                  onChange={() => { setAllMatching(false); setSelected((cur) => { const n = new Set(cur); if (allOnPage) items.forEach((i) => n.delete(i.id)); else items.forEach((i) => n.add(i.id)); return n; }); }} /></th>
                <th>Produto</th><th>Marca</th><th>Categoria</th><th className="num">Preço aqui</th><th className="num">Estoque aqui</th><th>Situação</th>
              </tr></thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id} onClick={() => toggle(i.id)} style={{ cursor: 'pointer' }}>
                    <td><input type="checkbox" className="check" checked={allMatching || selected.has(i.id)} onChange={() => toggle(i.id)} onClick={(e) => e.stopPropagation()} aria-label={`Marcar ${i.sku}`} /></td>
                    <td><span className="code">{i.sku}</span> · {i.title}</td>
                    <td>{i.brand ?? '—'}</td><td>{i.category ?? '—'}</td>
                    <td className="num">{i.price != null ? brl(i.price) : '—'}</td>
                    <td className="num">{i.stock != null ? int(i.stock) : '—'}</td>
                    <td>{i.blockedBy ? <span title={i.blockedBy}><Tag tone="err">bloqueado</Tag></span> : <Tag tone="ok">liberado</Tag>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {res.data!.total > 50 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
              <button className="btn sm" type="button" disabled={page === 1} onClick={() => setPage(page - 1)}>Anterior</button>
              <span className="small">{page} / {Math.ceil(res.data!.total / 50)}</span>
              <button className="btn sm" type="button" disabled={page * 50 >= res.data!.total} onClick={() => setPage(page + 1)}>Próxima</button>
            </div>
          )}
          <p className="small muted" style={{ margin: 0 }}>"Liberar" tira só o bloqueio do produto; se ele estiver bloqueado por uma regra (marca, categoria…), a regra continua — veja a lista abaixo.</p>
        </>
      )}
    </section>
  );
}

function Rules({ data }: { data: ChannelSettingsResponse }) {
  const admin = can(useMe().tenant.access, 'channel_settings', 'write');
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['meli'] });
  const add = useMutation({
    mutationFn: (b: { scope: 'category'; category: string; marginPct: number } | { scope: 'product'; productId: string; marginPct: number }) =>
      api(`/channels/accounts/${data.accountId}/rules`, { method: 'POST', json: b }),
    onSuccess: refresh,
  });
  const del = useMutation({ mutationFn: (id: string) => api(`/channels/rules/${id}`, { method: 'DELETE' }), onSuccess: refresh });
  const cats = useQuery({ queryKey: ['categories'], queryFn: () => api<CategoryCount[]>('/catalog/categories') });
  const [cat, setCat] = useState('');
  const [catMargin, setCatMargin] = useState('');
  const [prodQ, setProdQ] = useState('');
  const [prodMargin, setProdMargin] = useState('');
  const [picked, setPicked] = useState<ProductRow | null>(null);
  const found = useQuery({
    queryKey: ['rule-pick', prodQ],
    queryFn: () => api<Page<ProductRow>>(`/catalog/products?q=${encodeURIComponent(prodQ)}&pageSize=6`),
    enabled: prodQ.trim().length >= 2 && !picked,
  });
  const categoryRules = data.rules.filter((r) => r.scope === 'category');
  const productRules = data.rules.filter((r) => r.scope === 'product');
  const general = data.settings.defaultMarginPct;

  return (
    <div className="stack-y">
      <p className="small muted" style={{ margin: 0 }}>
        A regra mais específica vence: <b>produto</b> &gt; <b>categoria</b> &gt; <b>geral</b> ({general.toLocaleString('pt-BR')}%). Preço na mão e buy box aplicado passam por cima de todas.
      </p>
      <div className="cols">
        <section className="panel stack-y" style={{ gap: 12 }}>
          <h2 style={{ margin: 0 }}>Por categoria</h2>
          {categoryRules.length === 0 && <span className="small muted">Nenhuma regra por categoria.</span>}
          {categoryRules.map((r) => (
            <div className="row-line" key={r.id}>
              <span>{r.category}</span>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <b className="tnum">{r.marginPct.toLocaleString('pt-BR')}%</b>
                {admin && <button className="icon-btn" type="button" aria-label="Remover regra" onClick={() => del.mutate(r.id)}><Trash2 size={15} /></button>}
              </span>
            </div>
          ))}
          {admin && (
            <form style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} onSubmit={(e) => { e.preventDefault(); if (cat && catMargin) add.mutate({ scope: 'category', category: cat, marginPct: num(catMargin) }, { onSuccess: () => { setCat(''); setCatMargin(''); } }); }}>
              <select className="select" value={cat} onChange={(e) => setCat(e.target.value)} style={{ flex: '1 1 180px' }} aria-label="Categoria">
                <option value="">Categoria…</option>
                {cats.data?.map((c) => <option key={c.category} value={c.category}>{c.category}</option>)}
              </select>
              <input className="input" placeholder="Margem %" inputMode="decimal" value={catMargin} onChange={(e) => setCatMargin(e.target.value)} style={{ width: 110 }} aria-label="Margem %" />
              <button className="btn" type="submit" disabled={!cat || !catMargin || add.isPending}><Plus size={14} /> Adicionar</button>
            </form>
          )}
        </section>

        <section className="panel stack-y" style={{ gap: 12 }}>
          <h2 style={{ margin: 0 }}>Por produto</h2>
          {productRules.length === 0 && <span className="small muted">Nenhuma regra por produto.</span>}
          {productRules.map((r) => (
            <div className="row-line" key={r.id}>
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <span className="code">{r.product?.sku}</span> · {r.product ? titleOf(r.product) : ''}
              </span>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <b className="tnum">{r.marginPct.toLocaleString('pt-BR')}%</b>
                {admin && <button className="icon-btn" type="button" aria-label="Remover regra" onClick={() => del.mutate(r.id)}><Trash2 size={15} /></button>}
              </span>
            </div>
          ))}
          {admin && (
            <form style={{ display: 'grid', gap: 8 }} onSubmit={(e) => { e.preventDefault(); if (picked && prodMargin) add.mutate({ scope: 'product', productId: picked.id, marginPct: num(prodMargin) }, { onSuccess: () => { setPicked(null); setProdQ(''); setProdMargin(''); } }); }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {picked ? (
                  <span className="notice small" style={{ flex: '1 1 200px', padding: '6px 10px' }}>
                    {picked.sku} · {titleOf(picked)} <button type="button" className="btn quiet sm" onClick={() => { setPicked(null); }}>trocar</button>
                  </span>
                ) : (
                  <input className="input" placeholder="Buscar produto por SKU, EAN ou nome" value={prodQ} onChange={(e) => setProdQ(e.target.value)} style={{ flex: '1 1 200px' }} />
                )}
                <input className="input" placeholder="Margem %" inputMode="decimal" value={prodMargin} onChange={(e) => setProdMargin(e.target.value)} style={{ width: 110 }} aria-label="Margem %" />
                <button className="btn" type="submit" disabled={!picked || !prodMargin || add.isPending}><Plus size={14} /> Adicionar</button>
              </div>
              {!picked && found.data && found.data.items.length > 0 && (
                <div style={{ display: 'grid', gap: 4 }}>
                  {found.data.items.map((p) => (
                    <button key={p.id} type="button" className="btn sm" style={{ justifyContent: 'flex-start' }} onClick={() => setPicked(p)}>
                      <span className="code">{p.sku}</span> · {titleOf(p)} {p.isKit && <Tag tone="info">kit</Tag>}
                    </button>
                  ))}
                </div>
              )}
            </form>
          )}
        </section>
      </div>
      {(add.error || del.error) && <ErrorBox error={add.error ?? del.error} />}
    </div>
  );
}

interface ShopeeLogistics { arrange: boolean; method: 'pickup' | 'dropoff' | 'auto'; docType: 'THERMAL_AIR_WAYBILL' | 'NORMAL_AIR_WAYBILL'; dpi: 203 | 300; labelSize: '10x15' | '10x10' }

/** Shopee: a Berytus organiza o envio ao gerar a etiqueta? Coleta ou postagem? Etiqueta térmica ou A4. */
function ShopeeLogisticsForm({ accountId }: { accountId: string }) {
  const me = useMe();
  const admin = can(me.tenant.access, 'channel_settings', 'write') && !me.impersonating;
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['shopee', 'logistics', accountId], queryFn: () => api<ShopeeLogistics>(`/channels/accounts/${accountId}/logistics`) });
  const [f, setF] = useState<ShopeeLogistics | null>(null);
  useEffect(() => { if (q.data && !f) setF(q.data); }, [q.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = useMutation({
    mutationFn: (b: ShopeeLogistics) => api(`/channels/accounts/${accountId}/logistics`, { method: 'PUT', json: b }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shopee', 'logistics', accountId] }),
  });
  if (q.isPending || !f) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const dirty = JSON.stringify(f) !== JSON.stringify(q.data);
  return (
    <form className="panel stack-y" style={{ gap: 14, maxWidth: 760 }} onSubmit={(e) => { e.preventDefault(); save.mutate(f); }}>
      <h2 style={{ margin: 0 }}>Envio e etiqueta da Shopee</h2>
      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <input type="checkbox" className="check" style={{ marginTop: 3 }} checked={f.arrange} disabled={!admin} onChange={(e) => setF({ ...f, arrange: e.target.checked })} />
        <span><b>A Berytus organiza o envio ao gerar a etiqueta</b>
          <div className="small muted" style={{ marginTop: 4 }}>
            Quando a etiqueta é pedida (pelo ERP ou pelo botão no pedido) e o envio ainda não foi organizado, a Berytus organiza pela API, como o
            botão "Organizar envio → Confirmar" do Seller Center. Desmarcado: alguém organiza no Seller Center e a Berytus só baixa a etiqueta.
            A nota fiscal precisa ter sido enviada antes (a Shopee não libera o envio sem ela), menos na Entrega Turbo, em que a coleta sai antes da nota.
          </div>
        </span>
      </label>
      <Field label="Como entregar os pacotes" hint="A coleta usa o endereço de coleta da loja (senão o padrão) e o primeiro horário que a Shopee oferecer.">
        <select className="select" value={f.method} disabled={!admin || !f.arrange} onChange={(e) => setF({ ...f, method: e.target.value as ShopeeLogistics['method'] })}>
          <option value="pickup">Coleta na loja</option>
          <option value="dropoff">Postagem em agência / ponto (drop-off)</option>
          <option value="auto">O que a Shopee oferecer (coleta, se houver as duas)</option>
        </select>
      </Field>
      <Field label="Etiqueta" hint="Térmica: a Shopee manda o ZPL, que sai no tamanho 10x15 para a impressora térmica. A4: PDF para impressora comum. Cada loja da Shopee tem a sua configuração.">
        <select className="select" value={f.docType} disabled={!admin} onChange={(e) => setF({ ...f, docType: e.target.value as ShopeeLogistics['docType'] })}>
          <option value="THERMAL_AIR_WAYBILL">Térmica 10x15</option>
          <option value="NORMAL_AIR_WAYBILL">Folha A4</option>
        </select>
      </Field>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Field label="Impressora (DPI)">
          <select className="select" value={f.dpi} disabled={!admin} onChange={(e) => setF({ ...f, dpi: Number(e.target.value) as 203 | 300 })}>
            <option value={203}>203 dpi (padrão das Zebra de mesa)</option>
            <option value={300}>300 dpi</option>
          </select>
        </Field>
        <Field label="Tamanho da etiqueta">
          <select className="select" value={f.labelSize} disabled={!admin} onChange={(e) => setF({ ...f, labelSize: e.target.value as '10x15' | '10x10' })}>
            <option value="10x15">10 × 15 cm</option>
            <option value="10x10">10 × 10 cm</option>
          </select>
        </Field>
      </div>
      <span className="small muted">
        A etiqueta térmica da Shopee já vem em ZPL (203 dpi, 10 × 15) e vai como está. Quando a Shopee manda PDF, a Berytus desenha a página
        direto no DPI e no tamanho escolhidos (sem redimensionar depois), para o código de barras sair nítido.
      </span>
      {save.error && <ErrorBox error={save.error} />}
      {save.isSuccess && !dirty && <span className="small" style={{ color: 'var(--ok)' }}>Salvo.</span>}
      <div><button className="btn primary" type="submit" disabled={!admin || !dirty || save.isPending}>{save.isPending ? 'Salvando…' : 'Salvar'}</button></div>
    </form>
  );
}
